/**
 * 使用分析 —— 员工个人「具体使用情况」抽屉。
 *
 * ============================== 需求背景（2026-10-20 改版） ==============================
 * 老板的原话：点开一个人，抽屉显示的也**只是一个列表**，这个列表就是「这个人具体
 * 的使用情况」——访问了什么页面、点了什么按钮、做了什么操作。
 *
 * 所以本抽屉不再有统计卡片、趋势图、模块分布图、时段图和会话列表，只保留：
 *   ① 一行身份信息（姓名 / 部门 / 区间），让人确认点对了人；
 *   ② 一个逐条操作列表（分页）。
 * 上一版那些图表回答的是「用了多久」，而这个问题已经在列表页的每一行上直接给出了；
 * 抽屉要回答的是另一个问题：「他具体做了什么」。
 *
 * ============================== 隐私边界 ==============================
 * 每条记录只有：事件类型（打开页面 / 业务操作 / 登录 / 退出登录）、模块**业务名称**、
 * 动作类型、发生时间。绝不展示用户输入的文字、搜索关键词、聊天正文、文件名、
 * 客户信息，也没有屏幕截图。展示文案全部由服务端按固定枚举翻译。
 *
 * ============================== 审计 ==============================
 * 后端 /api/usage/employees/{username}/events 每次调用都写 audit_logs
 * （查看人 / 时间 / 筛选范围 / 页码），所以本抽屉只在打开和真正翻页时请求，
 * 不做任何定时轮询，避免审计日志被刷屏。
 */

import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Clock, Eye, Loader2, ShieldCheck, UserRound, Zap } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { CrmDetailDrawer } from "../crm/CrmDetailDrawer";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { fmtNumber, shortModuleName } from "../../lib/usageFormat";

/* 事件类型：与后端 EVENT_KINDS 一一对应，用于左侧小圆点配色。 */
const EVENT_STYLE = {
  view: { icon: Eye, cls: "bg-brand-50 text-brand-600 border-brand-200" },
  action: { icon: Zap, cls: "bg-violet-50 text-violet-600 border-violet-200" },
  login: { icon: UserRound, cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  logout: { icon: Clock, cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

/* 每页条数：抽屉只有 520px 宽，20 条一页既能看清单页节奏，又不必频繁翻页。 */
const TIMELINE_PAGE_SIZE = 20;

/* 抽屉内的区间档位。只放预设档，不做自定义日历（自定义属于列表页的筛选场景）。 */
const DRAWER_RANGES = [
  { key: "today", zh: "今天", en: "Today" },
  { key: "7d", zh: "7 天", en: "7d" },
  { key: "30d", zh: "30 天", en: "30d" },
  { key: "90d", zh: "90 天", en: "90d" },
];

/**
 * @param {string} username   要查看的员工账号（空串表示关闭）
 * @param {object} employee   列表里那一行的数据，用于头部身份信息（可选）
 * @param {string} range      初始区间，默认近 30 天
 * @param {string} start/end  自定义区间的起止（range=custom 时使用）
 * @param {string} tz         时区
 * @param {function} onClose  关闭回调
 */
export function UsageEmployeeDrawer({ username, employee = null, range: initialRange = "30d", start = "", end = "", tz = "Asia/Shanghai", onClose }) {
  const { lang, api } = useApp();
  const zh = lang === "zh";

  /* 时间范围由抽屉自己持有，初值取自调用方的 range。
     为什么抽屉要能自己改范围：这个抽屉有两个入口——
       ① 使用分析列表页：沿用列表页当前选中的区间；
       ② 系统管理 › 用户管理 › 账号列表：那里没有区间概念，默认近 30 天。
       注：2026-10-21 起「使用分析」本身也已并入「用户管理」分组，两者是并列的三级菜单；
       入口位置变了，但两个入口的默认区间差异不变。
     老板点开某人后经常要「看看今天」「看看近 7 天」，总不能退回去再筛一遍。 */
  const [range, setRange] = useState(initialRange);

  /* 换人或调用方区间变了就回到初值，避免把上一个人的选择带到下一个人身上。 */
  useEffect(() => { setRange(initialRange); }, [initialRange, username]);

  const [timeline, setTimeline] = useState(null);
  const [timelineKind, setTimelineKind] = useState("all");
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");

  /* 当前页码只保存在服务端响应里（`timeline.page`），不再单独存一份 UI 状态：
     两份状态一旦不同步，就会出现「翻到第 3 页但高亮第 1 页」这种问题。 */
  const loadTimeline = useCallback(async (page = 1) => {
    if (!username) return;
    setTimelineLoading(true);
    setTimelineError("");
    try {
      const { data: d } = await api.get(`/usage/employees/${encodeURIComponent(username)}/events`, {
        params: {
          range, tz, page, pageSize: TIMELINE_PAGE_SIZE,
          ...(range === "custom" ? { start, end } : {}),
          ...(timelineKind !== "all" ? { kind: timelineKind } : {}),
        },
      });
      setTimeline(d);
    } catch (e) {
      setTimelineError(e?.detailText || (zh ? "无法加载操作记录" : "Failed to load activity"));
      setTimeline(null);
    } finally {
      setTimelineLoading(false);
    }
  }, [api, username, range, start, end, tz, zh, timelineKind]);

  // 换人 / 换区间 / 换类型都从第 1 页重新加载。
  useEffect(() => {
    if (!username) { setTimeline(null); return; }
    loadTimeline(1);
  }, [username, loadTimeline]);

  /* 头部身份信息优先用列表那一行，没有时退回账号本身。
     字段归一：两个入口的行数据字段名不同 ——
       · 使用分析列表页：name / departmentName / roleLabel / branchName
       · 系统设置 › 用户管理：nameZh / name / departmentName / role / region
     这里统一取一遍，避免从不同入口进来时副标题空掉。 */
  const displayName = employee?.name || employee?.nameZh || username || (zh ? "操作记录" : "Activity");
  // 岗位文案：使用分析页的「主要」行自带 roleLabel；用户管理行只有 role / baseRole 代码，
  // 没有映射表时宁可显示代码也不显示空白（不引入第二份角色字典，避免与权限模块漂移）。
  const roleText = employee?.roleLabel || employee?.customRoleLabel || employee?.baseRole || employee?.role || "";
  const subtitle = employee
    ? Array.from(new Set([
      employee.departmentName || (zh ? employee.departmentNameZh : employee.departmentNameEn),
      roleText,
      employee.branchName || employee.region,
    ].filter(Boolean))).join(" · ") || (zh ? "只展示行为记录，不包含任何输入内容" : "Behaviour log only — no input content")
    : (zh ? "只展示行为记录，不包含任何输入内容" : "Behaviour log only — no input content");

  /* CrmDetailDrawer 自身没有 open 属性（它就是「挂着即打开」的壳），
     所以关闭态必须在这里提前 return null —— 否则列表页一进来就会盖一层空抽屉。
     ⚠️ 这个 return 必须放在所有 Hook 之后：提前 return 会跳过 Hook 调用顺序，
        触发 "Rendered fewer hooks than expected" 崩溃。 */
  if (!username) return null;

  /* 抽屉外壳改用 CrmDetailDrawer —— 与「已签约客户详情」用的**完全同一个组件**
     （需求原话「抽屉参考已签约客户详情的抽屉，直接一模一样」）。
     这样三件事自动对齐，不必在这里重写一遍：
       ① 宽度：默认视口宽 × 70%，左边缘可拖拽调宽、双击复位、宽度写 localStorage 记忆；
       ② 关闭：右上角关闭按钮 + Esc + 点遮罩三种方式，多层级时 Esc 只关最上面那层；
       ③ 行为：打开期间锁背景滚动、手机满宽、避开系统安全区。
     层级由组件内部固定在 z-[45]（低于 Radix 弹窗 z-50），抽屉里再开弹窗不会被压住。 */
  return (
    <CrmDetailDrawer
      title={displayName}
      onClose={onClose}
      testId="usage-detail-drawer">
      <div data-testid="usage-employee-drawer">
        {/* 身份 / 区间一行：抽屉顶部细栏只放姓名（由 CrmDetailDrawer 提供），
            部门岗位与停用状态放这里，避免和标题挤在一行。 */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-800">
              <UserRound className="size-4 shrink-0 text-brand-600" aria-hidden="true" />
              <span className="min-w-0 truncate">{displayName}</span>
              {employee?.disabled && (
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">{zh ? "已停用" : "Disabled"}</span>
              )}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
          </div>

          {/* 区间切换：预设档（今天 / 7 / 30 / 90 天）。
              列表页带「自定义区间」进来时，预设档都不高亮，另显示一枚只读标记，
              说明当前看的是那个自定义区间，避免用户以为没生效。 */}
          <div className="inline-flex shrink-0 overflow-hidden rounded-lg border border-slate-200 text-xs" role="group"
            aria-label={zh ? "时间范围" : "Date range"} data-testid="usage-detail-ranges">
            {DRAWER_RANGES.map((r) => (
              <button key={r.key} type="button" data-testid={`usage-detail-range-${r.key}`}
                aria-pressed={range === r.key} onClick={() => setRange(r.key)}
                className={`min-h-8 px-2.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${range === r.key ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {zh ? r.zh : r.en}
              </button>
            ))}
            {!DRAWER_RANGES.some((r) => r.key === range) && (
              <span className="inline-flex min-h-8 items-center bg-brand-600 px-2.5 font-medium text-white" data-testid="usage-detail-range-custom">
                {zh ? "自定义" : "Custom"}{start && end ? ` ${start}~${end}` : ""}
              </span>
            )}
          </div>
        </div>

        {/* ============ 唯一的内容：具体使用情况列表（分页） ============
            需求：点开某个员工后，能看到他「访问了什么页面、点击了什么按钮、
            做了什么操作」的逐条列表，并且必须分页。 */}
        <section className="mt-4" data-testid="usage-detail-timeline">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-slate-700">{zh ? "具体使用情况" : "Activity log"}</p>
              <p className="text-[10px] text-slate-400">
                {zh ? "按时间倒序，只记录页面与动作类型，不含任何输入内容" : "Reverse chronological — pages and action types only"}
              </p>
            </div>
            <Select value={timelineKind} onValueChange={setTimelineKind}>
              <SelectTrigger className="h-8 w-[112px] text-xs" data-testid="usage-timeline-kind" aria-label={zh ? "事件类型" : "Event type"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{zh ? "全部类型" : "All types"}</SelectItem>
                {(timeline?.kinds || [
                  { key: "view", zh: "打开页面" }, { key: "action", zh: "业务操作" },
                  { key: "login", zh: "登录" }, { key: "logout", zh: "退出登录" },
                ]).map((k) => (
                  <SelectItem key={k.key} value={k.key}>{k.zh}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {timelineError && (
            <div className="mt-2 flex items-start justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert"
              data-testid="usage-timeline-error">
              <span className="inline-flex items-start gap-1.5"><AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />{timelineError}</span>
              <button type="button" onClick={() => loadTimeline(1)} className="shrink-0 font-medium underline">{zh ? "重试" : "Retry"}</button>
            </div>
          )}
          {timelineLoading && !timeline && (
            <div className="mt-2 flex min-h-32 items-center justify-center text-slate-400" data-testid="usage-timeline-loading">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            </div>
          )}
          {timeline && (timeline.items || []).length === 0 && !timelineLoading && (
            <div className="mt-2 flex min-h-32 flex-col items-center justify-center rounded-lg bg-slate-50 px-3 py-6 text-center" data-testid="usage-timeline-empty">
              <Clock className="size-5 text-slate-300" aria-hidden="true" />
              <p className="mt-2 text-[11px] text-slate-500">{zh ? "所选区间没有操作记录" : "No activity in range"}</p>
              <p className="mt-0.5 text-[10px] text-slate-400">{zh ? "逐条明细自功能启用日起采集，更早的记录不可还原" : "Detailed events are collected from the feature launch date"}</p>
            </div>
          )}
          {timeline && (timeline.items || []).length > 0 && (
            <>
              <ul className={`mt-2 space-y-1 ${timelineLoading ? "opacity-50" : ""}`} data-testid="usage-timeline-list">
                {timeline.items.map((ev, i) => {
                  const style = EVENT_STYLE[ev.kind] || EVENT_STYLE.view;
                  const Icon = style.icon;
                  return (
                    <li key={`${ev.at}-${i}`} className="flex items-start gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5 text-[11px]"
                      data-testid={`usage-timeline-item-${ev.kind}`}>
                      <span className={`mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border ${style.cls}`}>
                        <Icon className="size-3" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                          <span className="min-w-0 truncate text-slate-700">
                            {ev.kind === "action"
                              ? <><span className="font-medium text-violet-700">{ev.actionName || ev.action}</span>
                                {ev.moduleName ? <span className="text-slate-500"> · {shortModuleName(ev.moduleName, 16)}</span> : null}</>
                              : ev.kind === "login" ? (zh ? "登录系统" : "Signed in")
                                : ev.kind === "logout" ? (zh ? "退出登录" : "Signed out")
                                  : <><span className="text-slate-500">{zh ? "打开" : "Opened"}</span> {shortModuleName(ev.moduleName, 20)}</>}
                          </span>
                          <span className="shrink-0 tabular-nums text-[10px] text-slate-400">
                            {ev.localDate} {ev.localTime}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* 分页条：总条数 + 上一页 / 下一页。总数放在这里，老板能立刻知道
                  这个人到底有多少操作，而不是只看到当前 20 条。 */}
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-400 tabular-nums" data-testid="usage-timeline-count">
                  {zh ? `共 ${fmtNumber(timeline.total, zh)} 条 · 第 ${timeline.page} / ${timeline.totalPages} 页`
                    : `${timeline.total} events · page ${timeline.page}/${timeline.totalPages}`}
                </span>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="outline" size="sm" className="h-7 gap-0.5 px-2 text-[11px]"
                    disabled={timeline.page <= 1 || timelineLoading}
                    onClick={() => loadTimeline(timeline.page - 1)}
                    data-testid="usage-timeline-prev">
                    <ChevronLeft className="size-3.5" aria-hidden="true" />{zh ? "上一页" : "Prev"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="h-7 gap-0.5 px-2 text-[11px]"
                    disabled={timeline.page >= timeline.totalPages || timelineLoading}
                    onClick={() => loadTimeline(timeline.page + 1)}
                    data-testid="usage-timeline-next">
                    {zh ? "下一页" : "Next"}<ChevronRight className="size-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>

        <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-slate-50 px-2.5 py-2 text-[10px] leading-4 text-slate-500">
          <ShieldCheck className="mt-0.5 size-3 shrink-0 text-emerald-500" aria-hidden="true" />
          {zh
            ? "仅采集登录、页面可见性、模块访问和业务动作类型；不采集输入内容、聊天正文、文件内容、屏幕或音视频。本次查看已写入审计日志。"
            : "Only logins, page visibility, module visits and action types are recorded. This view has been written to the audit log."}
        </p>
      </div>
    </CrmDetailDrawer>
  );
}
